/**
 * Admin — Breach Register.
 * Records the mandatory fields per the operating spec + displays a live 72-hour
 * countdown from `discovered_at`. The application does NOT determine whether
 * ICO notification is legally required — that decision must be recorded by
 * a human via the "ICO notification required" toggle + notes.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const STATUS = {
  open:          { label: "Open",          cls: "text-red-400" },
  investigating: { label: "Investigating", cls: "text-amber-400" },
  contained:     { label: "Contained",     cls: "text-blue-400" },
  closed:        { label: "Closed",        cls: "text-emerald-400" },
};

const emptyBreach = {
  discovered_at: new Date().toISOString().slice(0, 16),
  reporter_name: "",
  description: "",
  information_affected: "",
  people_affected_count: 0,
  people_affected_categories: [],
  containment_action: "",
  risk_assessment: "",
  ico_notification_required: null,
  ico_notification_at: "",
  affected_notification_required: null,
  corrective_actions: "",
  responsible_owner: "",
};

/** Convert `discovered_at` → { remaining_hours, breach_status } */
function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!iso) return { hours: null, expired: false, pct: 0 };
    const start = new Date(iso).getTime();
    const elapsed = now - start;
    const total = 72 * 3600 * 1000;
    const remaining = total - elapsed;
    const hours = remaining / 3600000;
    return { hours, expired: hours <= 0, pct: Math.min(100, Math.max(0, (elapsed / total) * 100)) };
  }, [iso, now]);
}

function CountdownPill({ discovered_at, status }) {
  const c = useCountdown(discovered_at);
  if (status === "closed" || c.hours === null) return null;
  const cls = c.expired ? "text-red-500 border-red-500/40 bg-red-500/10"
            : c.hours < 12 ? "text-red-400 border-red-400/40 bg-red-400/10"
            : c.hours < 24 ? "text-amber-400 border-amber-400/40 bg-amber-400/10"
            : "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
  const label = c.expired ? "72h ELAPSED" : `${Math.floor(c.hours)}h remaining`;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${cls}`} data-testid="breach-countdown">
      <Clock size={10} className="inline-block mr-1 -translate-y-px" />{label}
    </span>
  );
}

export default function AdminBreaches() {
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyBreach);
  const [saving, setSaving] = useState(false);
  const [openBreach, setOpenBreach] = useState(null);
  const [editForm, setEditForm] = useState({});

  const refresh = async () => {
    setLoading(true);
    try { const { data } = await api.get("/breaches"); setBreaches(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.reporter_name || form.description.trim().length < 5 || !form.information_affected) {
      toast.error("Reporter, description (min 5 chars) and info affected are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/breaches", { ...form, discovered_at: new Date(form.discovered_at).toISOString() });
      toast.success("Breach recorded.");
      setShowNew(false);
      setForm(emptyBreach);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(editForm).map(([k, v]) => [k, v === "" ? null : v]));
      await api.patch(`/breaches/${openBreach.id}`, payload);
      toast.success("Updated.");
      setOpenBreach(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <div className="fl-label text-[#f47a20]">Compliance</div>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="fl-heading text-3xl md:text-4xl mt-2">Data breach register</h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Record and track suspected or confirmed data breaches. The 72-hour countdown starts at the time of discovery — it does NOT determine whether ICO notification is legally required. That decision must be recorded manually per your breach procedure.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} className="fl-btn text-sm shrink-0" data-testid="breach-new">
            <Plus size={12}/> New breach
          </button>
        </div>

        <div className="mt-6 space-y-2" data-testid="breach-list">
          {loading && <div className="fl-card p-6 text-sm text-zinc-500">Loading…</div>}
          {!loading && breaches.length === 0 && (
            <div className="fl-card p-8 text-center text-sm text-zinc-500">No breaches recorded — good news.</div>
          )}
          {breaches.map((b) => {
            const meta = STATUS[b.status] || STATUS.open;
            return (
              <button key={b.id} onClick={() => { setOpenBreach(b); setEditForm({ ...b }); }} className="fl-card p-4 w-full text-left hover:border-[#f47a20]/40 transition-colors" data-testid={`breach-row-${b.id}`}>
                <div className="flex items-start gap-4">
                  <AlertTriangle className={meta.cls} size={18} />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-zinc-100">{b.description.slice(0, 80)}{b.description.length > 80 ? "…" : ""}</span>
                      <span className={`text-[10px] uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
                      <CountdownPill discovered_at={b.discovered_at} status={b.status} />
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      Discovered {new Date(b.discovered_at).toLocaleString()} · Reporter {b.reporter_name} · {b.people_affected_count} people affected
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* New breach modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-[100]" data-testid="breach-new-modal">
          <div className="fl-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="fl-label text-[#f47a20]">New breach record</div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div>
                <div className="fl-label mb-1">Discovered at</div>
                <input type="datetime-local" className="fl-input" value={form.discovered_at} onChange={(e) => setForm({ ...form, discovered_at: e.target.value })} data-testid="breach-form-discovered"/>
              </div>
              <div>
                <div className="fl-label mb-1">Reporter</div>
                <input className="fl-input" value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} placeholder="Name of person who discovered" data-testid="breach-form-reporter"/>
              </div>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">Description</div>
                <textarea className="fl-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened, in plain English." data-testid="breach-form-description"/>
              </div>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">Information affected</div>
                <input className="fl-input" value={form.information_affected} onChange={(e) => setForm({ ...form, information_affected: e.target.value })} placeholder="e.g. Patient contact details, no clinical notes" data-testid="breach-form-info"/>
              </div>
              <div>
                <div className="fl-label mb-1">People affected (count)</div>
                <input type="number" min={0} className="fl-input" value={form.people_affected_count} onChange={(e) => setForm({ ...form, people_affected_count: Number(e.target.value) })} data-testid="breach-form-count"/>
              </div>
              <div>
                <div className="fl-label mb-1">People categories (comma-separated)</div>
                <input className="fl-input" value={form.people_affected_categories.join(", ")} onChange={(e) => setForm({ ...form, people_affected_categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Patients, Staff, Contacts" data-testid="breach-form-cats"/>
              </div>
              <div>
                <div className="fl-label mb-1">Containment action</div>
                <input className="fl-input" value={form.containment_action || ""} onChange={(e) => setForm({ ...form, containment_action: e.target.value })} data-testid="breach-form-containment"/>
              </div>
              <div>
                <div className="fl-label mb-1">Responsible owner</div>
                <input className="fl-input" value={form.responsible_owner || ""} onChange={(e) => setForm({ ...form, responsible_owner: e.target.value })} data-testid="breach-form-owner"/>
              </div>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">Risk assessment</div>
                <textarea className="fl-input" rows={2} value={form.risk_assessment || ""} onChange={(e) => setForm({ ...form, risk_assessment: e.target.value })} data-testid="breach-form-risk"/>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="fl-btn-ghost text-sm" onClick={() => setShowNew(false)} data-testid="breach-form-cancel">Cancel</button>
              <button className="fl-btn text-sm" onClick={create} disabled={saving} data-testid="breach-form-save">
                {saving ? <Loader2 size={12} className="animate-spin"/> : "Record breach"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit breach modal */}
      {openBreach && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-[100]" data-testid="breach-edit-modal">
          <div className="fl-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="fl-label text-[#f47a20]">Breach · {openBreach.id.slice(0, 8)}</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Discovered {new Date(openBreach.discovered_at).toLocaleString()} · Reporter {openBreach.reporter_name}
            </div>

            <div className="mt-4 fl-label">Status</div>
            <select className="fl-input" value={editForm.status || "open"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} data-testid="breach-edit-status">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>

            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={!!editForm.ico_notification_required} onChange={(e) => setEditForm({ ...editForm, ico_notification_required: e.target.checked })} data-testid="breach-edit-ico"/>
                ICO notification required
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={!!editForm.affected_notification_required} onChange={(e) => setEditForm({ ...editForm, affected_notification_required: e.target.checked })} data-testid="breach-edit-affected"/>
                Affected individuals must be notified
              </label>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">ICO notification sent at</div>
                <input type="datetime-local" className="fl-input" value={editForm.ico_notification_at || ""} onChange={(e) => setEditForm({ ...editForm, ico_notification_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="breach-edit-ico-at"/>
              </div>
              <div className="md:col-span-2">
                <div className="fl-label mb-1">Corrective actions</div>
                <textarea className="fl-input" rows={2} value={editForm.corrective_actions || ""} onChange={(e) => setEditForm({ ...editForm, corrective_actions: e.target.value })} data-testid="breach-edit-corrective"/>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="fl-btn-ghost text-sm" onClick={() => setOpenBreach(null)} data-testid="breach-edit-cancel">Cancel</button>
              <button className="fl-btn text-sm" onClick={saveEdit} disabled={saving} data-testid="breach-edit-save">
                {saving ? <Loader2 size={12} className="animate-spin"/> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
